import styled, { StyledComponent } from 'styled-components';
import _ from 'lodash';
import { KeyboardEvent, useState } from 'react';
import { Input } from './Input';
import { CheckIcon, CloseIcon } from '../../Icons';
import { useStyledTheme, ThemeType } from '../../../styling';
import { CenterFlex } from '../../Flex';
import { useFormConfigurationContext } from '../FormConfigurationProvider';

const ToggleButtonContainer = styled.div`${({ theme }) => `
  display: inline-flex;
  align-items: center;
  position: relative;
  display: block;
  width: ${theme.padding[9]};
`}`;

export const ToggleButtonIconWrapperBase = styled.div<{ checked?: boolean, hasError?: boolean, disabled?: boolean }>`${({
  theme, checked, hasError, disabled,
}) => `
  background: ${theme.primary};
  color: ${checked ? theme.colours.primary[1] : theme.colours.neutral[4]};
  display: flex;
  justify-content: center;
  align-items: center;
  content: "";
  height: ${theme.padding[3]};
  width: ${theme.padding[3]};
  -webkit-transition: .4s;
  transition: .4s;
  border-radius: 50%;
  padding:2px;

  ${_.isNil(checked) && !disabled && hasError && `  
    border: ${theme.borderWidth[1]} solid ${theme.textDanger};
  `};
`}`;

export const ToggleButtonIconWrapperChecked = (theme : ThemeType) => `
  background: ${theme.primary};
  margin-left: auto;
  margin-right: 0;
`;

export const ToggleButtonIconWrapperUnchecked = (theme: ThemeType) => `
  background: ${theme.primary};
  margin-right: auto;
  margin-left: 0;
`;

const ToggleButtonIconWrapper = styled(ToggleButtonIconWrapperBase)<{checked?: boolean}>`${({
  theme, checked,
}) => `
  ${(checked === true) && ToggleButtonIconWrapperChecked(theme)};
  ${(checked !== true) && ToggleButtonIconWrapperUnchecked(theme)};
`}`;

export const StyledToggleButton = styled.div.attrs<{disabled?: boolean, checked?: boolean, hasError?: boolean, styles?: string}>(({ disabled = false }) => ({
  ...(disabled ? {} : { tabIndex: '0' }),
}))<{ disabled?: boolean, checked?: boolean, hasError?: boolean, styles?: string }>` ${({
  checked, theme, disabled, styles, hasError,
}) => `
  ${styles || ''}
  padding: calc(${theme.padding[1]} - ${theme.borderWidth[1]});
  cursor: pointer;
  background: ${checked ? theme.colours.primary[1] : theme.colours.neutral[4]};
  ${_.isNil(checked) && `
    background-color:${theme.colours.neutral[5]};
  `};
  ${disabled && `
    background-color: ${theme.colours.neutral[7]} !important;
    cursor: default;
  `};
  transition: .4s;
  border-radius: ${theme.padding[8]};

  ${(!disabled) && `
    &:focus {
      box-shadow: 0px 0px 0px 5px ${theme.colours.info[1]};
      outline: 2px solid ${theme.colours.neutral[10]};
    }
  `};

  ${!disabled && hasError && `
    border: ${theme.borderWidth[1]} solid ${theme.textDanger};
  `};
`}`;

export type ToggleButtonProps = {
  id: string,
  value?: boolean,
  errors?: string[],
  onChange: any,
  disabled?: boolean,
  visible?: boolean;
  touchOverride?: boolean;
  containerStyles?: string;
  styles?: string;
  onClickOverride?: () => void;
  IconWrapper?: StyledComponent<any, any, { checked?: boolean }, never>,
  isTooltipError?: boolean,
}

function IconClassName(checked?: boolean, disabled?: boolean) {
  const theme = useStyledTheme();
  if (checked === true) return (<CheckIcon fill={disabled ? theme.colours.neutral[7] : theme.colours.primary[1]} />);
  if (checked === false) return (<CloseIcon fill={disabled ? theme.colours.neutral[7] : theme.colours.neutral[4]} />);
  return '';
}

export function ToggleInput({
  id,
  value,
  errors = [],
  onChange,
  styles,
  disabled = false,
  visible = true,
  touchOverride = false,
  containerStyles,
  onClickOverride,
  IconWrapper = ToggleButtonIconWrapper,
  isTooltipError = false,
}: ToggleButtonProps) {
  const [touched, setTouched] = useState(false);
  const [tooltipError, setTooltipError] = useState(isTooltipError);
  const hasError = (touched || touchOverride) && !!errors?.length;

  const { isDisabled } = useFormConfigurationContext(disabled);

  const onClick = onClickOverride || (() => {
    if (!isDisabled) onChange(!value);
  });

  const onKeyDown = (event: KeyboardEvent) => {
    if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onChange(!value);
      setTouched(false);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setTooltipError(true);
  };

  return (
    <>
      {visible && (
        <Input
          id={id}
          errors={errors}
          hasError={hasError}
          containerStyles={containerStyles}
          isTooltipError={tooltipError}
          hideErrorMessage={!tooltipError}
        >
          <CenterFlex>
            <ToggleButtonContainer
              id={`${id}-toggle-button-container`}
              onClick={onClick}
              onKeyDown={onKeyDown}
              onBlur={handleBlur}
            >
              <StyledToggleButton checked={value} id={`${id}-styled-toggle-button`} styles={styles} disabled={isDisabled} hasError={hasError}>
                <IconWrapper checked={value} disabled={isDisabled} hasError={hasError}>
                  {IconClassName(value, isDisabled)}
                </IconWrapper>
              </StyledToggleButton>
            </ToggleButtonContainer>
          </CenterFlex>
        </Input>
      )}
    </>
  );
}
